/**
 * English strings for the analytics screens.
 *
 * Written out in full and derived from nothing — see en/index.ts. Typed as
 * a complete slice, so a key missing here is a build error rather than a
 * screen that quietly renders Bosnian.
 *
 * The reader is a manager or an owner rather than a worker at a machine, so
 * the sentences under the charts are allowed to be opinionated in the way the
 * Bosnian ones are. They are there to say what a chart is *for*; flattening
 * them into neutral captions would leave the page a wall of numbers with
 * nothing pointing at the finding.
 *
 * Work centre names stay as they are — "Hoblanje", "Montaža" — because they
 * are the names on the floor and on the chart next to the sentence quoting
 * them.
 */

import type { AnalyticsSlice } from '../bs/analytics'

export const analytics: AnalyticsSlice = {
  // ---- Page chrome -------------------------------------------------
  'analytics.title': 'Analytics',
  'analytics.subtitle': 'Reports, how they get resolved, and 5S scores',
  'analytics.period.label': 'Period',

  'analytics.range.days30': 'Last 30 days',
  'analytics.range.months3': 'Last 3 months',
  'analytics.range.months12': 'Last 12 months',

  'analytics.export': 'Export analytics',

  'analytics.tab.label': 'Sections of analytics',
  'analytics.tab.overview': 'Overview',
  'analytics.tab.submissions': 'Reports',
  'analytics.tab.fiveS': '5S',
  'analytics.tab.people': 'People',
  'analytics.plant.label': 'Plant',
  'analytics.plant.all': 'All plants',

  // ---- KPI tiles ---------------------------------------------------
  'analytics.kpi.label': 'Key figures',
  'analytics.kpi.created': 'New reports',
  'analytics.kpi.resolved': 'Resolved',
  'analytics.kpi.openNow': 'Open now',
  'analytics.kpi.overdue': 'Past due',
  'analytics.kpi.avgResolution': 'Average time to resolve',
  'analytics.kpi.fiveS': '5S average',
  'analytics.kpi.fiveS.hint': 'latest score per centre',
  'analytics.kpi.improvements': 'Proposals',
  'analytics.kpi.improvements.hint': '{count} accepted',
  'analytics.kpi.awards': 'Awards paid out',

  'kpi.previous': 'previous period: {value}',
  'kpi.atStart': 'at the start of the period: {value}',
  'kpi.direction.up': 'up on before',
  'kpi.direction.down': 'down on before',
  'kpi.direction.flat': 'no change',
  'kpi.points': '{value} pts',

  // ---- Series names ------------------------------------------------
  'analytics.series.reported': 'Reported',
  'analytics.series.resolved': 'Resolved',
  'analytics.series.total': 'Total',
  'analytics.series.open': 'Open',
  'analytics.series.overdue': 'Past due',
  'analytics.series.submissions': 'Reports',
  'analytics.series.proposals': 'Proposals',

  // ---- Volume and breakdown charts ---------------------------------
  'analytics.overTime.title': 'Reports over time',
  'analytics.overTime.empty': 'No data for the chosen period',
  'analytics.byType.title': 'By type of report',
  'analytics.empty.noSubmissions': 'No reports',
  'analytics.timeInStatus.title': 'Average time per status',
  'analytics.timeInStatus.hint':
    'Shows where reports sit and wait — the longest bar is the bottleneck.',
  'analytics.timeInStatus.explain':
    'A stay is counted only once a report leaves the status, so whatever is sitting in one right now is in none of the bars.',
  'analytics.timeInStatus.empty': 'No status changes yet',
  'analytics.timeInStatus.empty.description':
    'Data appears here once reports start moving between statuses.',
  'analytics.timeInStatus.tooltip': 'Average',
  'analytics.byWorkCenter.title': 'Reports by work centre',

  // ---- Process analysis --------------------------------------------
  'analytics.process.title': 'The process itself',
  'analytics.process.firstResponse': 'To first response',
  'analytics.process.firstResponse.hint': 'How long a report waits for somebody to pick it up',
  'analytics.process.resolution': 'To resolution',
  'analytics.process.resolution.hint': 'From the report to the “resolved” status',
  'analytics.process.accepted': 'Proposals accepted',
  'analytics.process.accepted.hint': '{count} awaiting a decision',
  'analytics.process.implemented': 'Carried out, of those accepted',
  'analytics.process.implemented.hint':
    'Accepted and then never done is what stops people proposing',

  // ---- Backlog age -------------------------------------------------
  'analytics.backlog.title': 'Age of the open reports',
  'analytics.backlog.hint':
    '“42 open” tells you nothing. How many of them are more than a week old does.',
  'analytics.backlog.explain':
    'Measured from the day of the report to today, and it covers every open report rather than only those in the chosen period.',
  'analytics.backlog.empty': 'No open reports',

  // ---- Improvement funnel ------------------------------------------
  'analytics.funnel.title': 'How far a proposal gets',
  'analytics.funnel.hint':
    'From proposed to carried out — and how much of the estimated saving was actually made.',
  'analytics.funnel.empty': 'No proposals in this period',
  'analytics.funnel.estimatedSavings': 'Estimated saving',
  'analytics.funnel.actualSavings': 'Actual saving',
  'analytics.funnel.estimatedCost': 'Estimated cost',
  'analytics.funnel.rejected': 'Rejected',

  // ---- Reporting rhythm --------------------------------------------
  'analytics.rhythm.title': 'When reports come in',
  'analytics.rhythm.hint':
    'It follows the shifts. A shift that reports nothing either has no breakdowns — or is not reporting them.',

  // ---- Assignee load -----------------------------------------------
  'analytics.assignees.title': 'Load on the people assigned',
  'analytics.assignees.hint':
    'Ten open of which none is late is healthy. Three, two of them late, is not.',
  'analytics.assignees.empty': 'No open reports are assigned to anyone',

  // ---- Recurring issues --------------------------------------------
  'analytics.recurring.title': 'What keeps coming back',
  'analytics.recurring.hint':
    'Work centre and type together. “Hoblanje has 40 reports” is noise; “Hoblanje has 19 breakdowns” is a machine worth going to look at.',
  'analytics.recurring.empty': 'No data for this period',

  // ---- Top contributors --------------------------------------------
  'analytics.contributors.title': 'Most active employees',
  'analytics.contributors.hint':
    'Who reports the most, and how many points they earned in this period.',
  'analytics.contributors.empty': 'No reports in this period',
  'analytics.contributors.unit': 'reports',

  // ---- 5S ----------------------------------------------------------
  'analytics.fiveS.title': '5S scores',
  'analytics.fiveS.year': 'Year',
  'analytics.fiveS.missing.title': 'Work centres with no score this month',
  'analytics.fiveS.missing.hint':
    'A high average across some of the centres is not a good month — score the rest as well.',
  'analytics.fiveS.trend.title': 'Trend by work centre',
  'analytics.fiveS.trend.hint':
    'Shown as percentages. Montaža is scored 1–3 and the other centres 1–5, so the raw averages do not compare.',
  'analytics.fiveS.byWorker.title': 'Trend by worker',
  'analytics.fiveS.byWorker.hint':
    'The average of the work centres a worker looked after, by month. It is the centre being scored, not the worker — somebody who took on a neglected centre will show a low percentage for exactly the reason they were sent there.',
  'analytics.fiveS.byWorker.empty': 'No data per worker',
  'analytics.fiveS.byWorker.empty.description':
    'Workers are chosen when a 5S evaluation is entered. Evaluations with no workers recorded do not show up here.',

  // Two of these three are the same sentence: English has one plural form
  // where Bosnian, Croatian and Serbian have three, and the selector is shared.
  'analytics.fiveS.workers.hidden.one':
    'Showing the {shown} workers with the most evaluations; {count} more worker is not shown.',
  'analytics.fiveS.workers.hidden.few':
    'Showing the {shown} workers with the most evaluations; {count} more workers are not shown.',
  'analytics.fiveS.workers.hidden.many':
    'Showing the {shown} workers with the most evaluations; {count} more workers are not shown.',

  'analytics.fiveS.byCategory.title': 'Average by category',
  'analytics.fiveS.byCategory.explain':
    'The average of every answer in the category for the chosen year, as a percentage of the form’s scale — Montaža is scored 1–3 and the other centres 1–5, so raw scores do not share an axis.',
  'analytics.fiveS.noEvaluations': 'No evaluations submitted',
  'analytics.fiveS.worst.title': 'The lowest-scoring criteria',
  'analytics.fiveS.worst.explain':
    'Ranked by the average across the whole year, lowest first — a criterion scored once sits here beside one scored fifty times.',
  'analytics.fiveS.worst.empty': 'No evaluations submitted for this year',
  'analytics.fiveS.worst.criterion': 'Criterion',
  'analytics.fiveS.worst.category': 'Category',
  'analytics.fiveS.worst.template': 'Form',
  'analytics.fiveS.worst.average': 'Average',

  // ---- Pager -------------------------------------------------------
  'analytics.pager.empty': 'No records',
  'analytics.pager.range': '{from}–{to} of {total}',
  'analytics.pager.perPage': '{count} per page',
  'analytics.pager.perPage.label': 'Rows per page',
  'analytics.pager.prev': 'Previous page',
  'analytics.pager.next': 'Next page',

  // ---- 5S by shift -------------------------------------------------
  'analytics.shifts.title': 'By shift',
  'analytics.shifts.subtitle':
    'Whether it matters who is on. If every shift keeps a machine the same way the problem is the machine; if the gap is wide the problem is the shift.',
  'analytics.shifts.explain':
    'Everything here is built from walks that name a shift, so until shifts exist and walks carry one there is nothing to show.',
  'analytics.shifts.walkCount.one': '{count} walk',
  'analytics.shifts.walkCount.few': '{count} walks',
  'analytics.shifts.walkCount.many': '{count} walks',
  'analytics.shifts.centreCount.one': '{count} centre',
  'analytics.shifts.centreCount.few': '{count} centres',
  'analytics.shifts.centreCount.many': '{count} centres',
  'analytics.shifts.thin': 'few walks',
  'analytics.shifts.unassigned.one':
    '{count} evaluation names no shift, so it is not in anything above.',
  'analytics.shifts.unassigned.few':
    '{count} evaluations name no shift, so they are not in anything above.',
  'analytics.shifts.unassigned.many':
    '{count} evaluations name no shift, so they are not in anything above.',
  'analytics.shifts.spread.title': 'Where the shifts disagree most',
  'analytics.shifts.spread.hint':
    'Machines one shift keeps markedly better than another. The average hides this — here it shows.',
  'analytics.shifts.spread.explain':
    'The gap is between the best and the weakest shift’s average on that centre, and a centre enters the list only once at least two shifts have walked it in the period.',
  'analytics.shifts.spread.gap': '{gap} apart',
  'analytics.shifts.trend.title': 'Shift by shift, month by month',
  'analytics.shifts.categories.title': 'What each shift is weakest at',
  'analytics.shifts.categories.hint':
    'Scores are converted to percentages, so Montaža (scale 1–3) is comparable with the other centres (1–5).',

  // ---- Per worker --------------------------------------------------
  'analytics.workforce.title': 'By person',
  'analytics.workforce.hint':
    'Three separate things, deliberately never summed. 5S describes the machines somebody was keeping, not the person; reports describe initiative; tasks describe follow-through and also how much work they were handed. A single total would be read as a performance score.',
  'analytics.workforce.rating.note':
    'Accepted proposals are added on top of the base and can only raise it — a refused proposal takes nothing away. Somebody who was on no walk, or who was given no task, is rated on the half they do have; the missing half does not count as a nought.',
  'analytics.workforce.undated.one':
    '{count} evaluation records no date, so it falls in no window.',
  'analytics.workforce.undated.few':
    '{count} evaluations record no date, so they fall in no window.',
  'analytics.workforce.undated.many':
    '{count} evaluations record no date, so they fall in no window.',
  'analytics.workforce.person': 'Person',
  'analytics.workforce.fiveS': '5S',
  'analytics.workforce.fiveS.hint':
    'The average of every walk this person is recorded on; the smaller figure beside it is the change against the previous period of the same length.',
  'analytics.workforce.walks': 'Walks',
  'analytics.workforce.walks.hint':
    'Submitted 5S walks this person is recorded on; below {walks}, the rating is marked as resting on very little.',
  'analytics.workforce.filed': 'Reports',
  'analytics.workforce.accepted': 'Accepted',
  'analytics.workforce.accepted.hint':
    'This is the only one of the three that moves the rating — how much was filed and how many points it earned do not.',
  'analytics.workforce.points': 'Points',
  'analytics.workforce.tasks': 'Tasks',
  'analytics.workforce.tasks.hint':
    'Finished, out of those assigned within this period; tasks from earlier periods are in neither figure.',
  'analytics.workforce.overdue': 'Late',
  'analytics.workforce.doneOf': '{done} / {total}',
  'analytics.shifts.month': 'Month',
  'analytics.shifts.wholeYear': 'Whole year',
  'analytics.shifts.none.title': 'No shifts defined yet',
  'analytics.shifts.none.description':
    'Once you add shifts, the evaluator picks one on every 5S walk and the comparison appears here — which shift keeps which machine better.',
  'analytics.shifts.none.action': 'Add shifts',
  'analytics.shifts.unused.title': 'No evaluation names a shift yet',
  'analytics.shifts.unused.description':
    'The shifts are defined. The comparison appears after the first 5S walk that picks one — earlier walks stay without.',
  'analytics.shifts.unused.action': 'New 5S walk',
  'analytics.workforce.search': 'Name or works number',
  'analytics.workforce.search.hint':
    'The table holds everybody active, including the people with nothing at all in this period, so that the quiet ones can be noticed.',
  'analytics.workforce.role': 'Role',
  'analytics.workforce.allRoles': 'All roles',
  'analytics.workforce.role.hint':
    'A role does not change how a rating is worked out — everybody is scored by the same formula, so this only narrows the list.',
  'analytics.workforce.workCenter': 'Work centre',
  'analytics.workforce.allCentres': 'All work centres',
  'analytics.workforce.workCenter.hint':
    'Picks the people assigned to that centre; the number beside a rating is still their place among everyone you can see, because the ranking is worked out before anything is filtered away.',
  'analytics.workforce.ratedOnly': 'Rated only',
  'analytics.workforce.ratedOnly.hint': 'Hide the people there is not enough data to score.',
  'analytics.workforce.empty': 'No workers match these filters.',
}
